/**
 * canvas_field_wrapper.jsx
 *
 * 所有畫布 element type 的共用包裝元件。
 * 取代原本各 case 中散落的 max-w-md / flex justify-center / mb-2 重複結構。
 *
 * 設計原則：
 * - 填寫/展示模式下 w-full，讓元件撐滿容器，由父層控制最大寬度
 * - 設計模式維持原本行為（pointer-events-auto + 滿版）
 * - Label、helpText 統一樣式，不再各自硬寫
 */

import React from "react"
import { Label } from "../ui/label"

/**
 * FieldWrapper
 *
 * @param {string}  label       - 欄位標籤（code 或 alt）
 * @param {boolean} required    - 是否必填（顯示 * 標記）
 * @param {string}  helpText    - 說明文字
 * @param {string}  className   - 額外 class
 * @param {React.ReactNode} children - 實際輸入元件
 */
export const FieldWrapper = ({
    label,
    required = false,
    helpText,
    className = "",
    children,
    isFillMode = true,   // design 模式傳 false，不加 padding 避免超出設計框
}) => {
    return (
        <div className={`field-wrapper w-full flex flex-col pointer-events-auto ${className}`}
            style={isFillMode ? { padding: '10px 12px' } : {}}>
            {label && (
                <label className="field-label" style={{
                    display: 'block',
                    fontSize: isFillMode ? '12px' : '12px',
                    color: '#6b7280',
                    fontWeight: 400,
                    lineHeight: 1.4,
                    marginBottom: isFillMode ? '3px' : '2px',
                    letterSpacing: '0.2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}>
                    {label}
                    {required && <span style={{ color: '#ef4444', marginLeft: '2px' }}>*</span>}
                </label>
            )}
            <div className="field-control w-full">
                {children}
            </div>
            {helpText && (
                <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px', lineHeight: 1.3 }}>
                    {helpText}
                </p>
            )}
        </div>
    )
}

/**
 * ElementShell
 *
 * 最外層殼，讓 element 在填寫模式下撐滿容器格
 * 設計模式保留 pointer-events-auto（拖放需要）
 *
 * @param {boolean} isFillMode  - true = fill / fill_disable 模式
 * @param {string}  align       - "start" | "center" | "end"（水平對齊，設計模式用）
 * @param {string}  className
 */
export const ElementShell = ({
    isFillMode = false,
    align = "start",
    className = "",
    children,
}) => {
    const alignClass = {
        start: "items-start",
        center: "items-center",
        end: "items-end",
    }[align] ?? "items-start"

    return (
        <div
            className={[
                "element-shell w-full h-full pointer-events-auto",
                isFillMode
                    ? "flex flex-col justify-start"
                    : `flex flex-col ${alignClass}`,
                className,
            ].join(" ")}
        >
            {children}
        </div>
    )
}

/**
 * TitleShell
 *
 * 專給 title element 用，支援 justifyContent 對齊
 */
export const TitleShell = ({ justifyContent = "flex-start", className = "", children }) => {
    return (
        <div
            className={`title-shell w-full h-full pointer-events-auto flex ${className}`}
            style={{ justifyContent }}
        >
            {children}
        </div>
    )
}

export default FieldWrapper
